import React, { useContext, useEffect, useRef, useState } from 'react';
import { AppContext } from '../../App';
import { useIsLabViewer } from '../../hooks/useIsLabViewer';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import { Col, Spinner } from 'react-bootstrap';
import Table from 'react-bootstrap/Table';
import DeleteButton from '../Generic/DeleteButton';
import {
  deleteSupplier,
  getSupplierDetails,
  getSupplierDetailsLazy,
} from '../../clients/supplier_client';
import SupplierModal from './SupplierModal';
import ProductDetailModal from '../Product/ProductDetailModal';
import ManufacturerDetailModal from '../Manufacturer/ManufacturerDetailModal';
import NavigateToUrlIcon from '../Generic/NavigateToUrlIcon';

/**
 * Represents a modal for displaying supplier details.
 *
 * @param {MutableRefObject} fetchingSuppliersRef - Reference variable for fetching suppliers.
 * @param {Function} setFetchedSuppliers - Function for setting fetched suppliers.
 * @param {boolean} isSupplier - Flag indicating if the supplier is already present.
 * @param {Object} supplierObj - Object representing the supplier.
 * @param {Function} updateSuppliers - Function for updating suppliers.
 * @param {string} supplierId - The ID of the supplier.
 * @param {Function} clearSearchValue - Function for clearing the search value.
 * @param {boolean} smallerFont - Flag indicating if the font size is smaller.
 * @returns {React.Element} The SupplierDetailModal component.
 */
const SupplierDetailModal = ({
  fetchedSuppliersRef,
  setFetchedSuppliers,
  isSupplier,
  supplierObj,
  updateSuppliers,
  supplierId,
  clearSearchValue,
  smallerFont,
  /** When true, prefetch full supplier record on trigger hover (table cells with id only). */
  prefetchOnHover = true,
}) => {
  const { token } = useContext(AppContext); // Fetching the token from the AppContext
  const isViewer = useIsLabViewer();
  const isLoadingRef = useRef(false); // Reference variable to hold loading state
  const [show, setShow] = useState(false); // State-variable for handling the visibility of the modal
  const [supplier, setSupplier] = useState(supplierObj); // State-variable for holding supplier data
  const supplierIdToUse = supplierId; // Always use the supplierId prop for fetching

  // Lazy loading states
  const [detailedData, setDetailedData] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [error, setError] = useState(null);

  // UseEffect to update the state on re-renders
  useEffect(() => {
    setSupplier(supplierObj);
  }, [supplierObj]);

  // Boolean to determine if the supplierId already exists in the fetched suppliers Ref
  const isSupplierInFetchedSuppliers = fetchedSuppliersRef
    ? fetchedSuppliersRef.current.has(supplierId)
    : false;

  // Function to fetch detailed supplier data with lazy loading
  const fetchDetailedData = async () => {
    if (!supplierId || detailedData || loadingDetails) {
      return; // Already loaded, loading, or no supplierId
    }

    setLoadingDetails(true);
    setError(null);

    try {
      const data = await getSupplierDetailsLazy(token, supplierId);
      setDetailedData(data);
    } catch (err) {
      setError(err.message || 'Failed to load supplier details');
    } finally {
      setLoadingDetails(false);
    }
  };

  // Function to fetch supplier details (only used when supplierId is provided)
  const fetchSupplier = () => {
    if (!supplierId || isSupplierInFetchedSuppliers) {
      return;
    }
    // Order/quote modals already pass supplierObj with a display name — defer to modal open.
    if (supplierObj?.name || supplier?.name) {
      return;
    }

    if (fetchedSuppliersRef) fetchedSuppliersRef.current.add(supplierId);

    isLoadingRef.current = true;

    getSupplierDetails(token, supplierIdToUse, setSupplier).then(response => {
      if (response.success && setFetchedSuppliers) {
        setFetchedSuppliers(prevFetchedSuppliers => [
          ...prevFetchedSuppliers,
          response.supplierData,
        ]);
      }
      isLoadingRef.current = false;
    });
  };

  // Only fetch on hover if we have supplierId but no supplier data

  const handleClose = () => setShow(false); // Function to hide the modal
  const handleShow = () => {
    setShow(true);
    // Fetch detailed data when modal opens
    fetchDetailedData();
  }; // Function to show the modal

  const displayName =
    supplier?.name || supplierObj?.name || (supplierId ? `Supplier ${supplierId}` : 'Supplier');

  return (
    <div>
      <Button
        variant="link"
        onClick={handleShow}
        onMouseEnter={() => {
          if (!prefetchOnHover) return;
          if (supplierId && !isSupplierInFetchedSuppliers) {
            fetchSupplier();
          }
        }}
        {...(smallerFont
          ? { style: { fontSize: '12px', padding: 0 } }
          : { style: { padding: 0 } })}
      >
        {displayName}
      </Button>

      {/* Modal uses supplier stub from props; detailed payload loads on open. */}
      {(supplier || supplierObj) && (
        <Modal
          show={show}
          onHide={() => setShow(false)}
          aria-labelledby="product-modal"
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>{displayName}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {(supplier || supplierObj) && !isLoadingRef.current && (
              <Container>
                {/* Loading state for detailed data */}
                {loadingDetails && (
                  <div className="text-center mb-3">
                    <Spinner animation="border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </Spinner>
                    <p className="mt-2">Loading supplier details...</p>
                  </div>
                )}

                {/* Error state */}
                {error && (
                  <div className="alert alert-danger mb-3">
                    <p className="mb-2">
                      Error loading supplier details: {error}
                    </p>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={fetchDetailedData}
                    >
                      Retry
                    </Button>
                  </div>
                )}

                {/* Fallback message when no detailed data */}
                {!loadingDetails && !error && !detailedData && (
                  <div className="alert alert-info mb-3">
                    <p className="mb-0">Loading supplier details...</p>
                  </div>
                )}

                {/* Display supplier details using detailed data when available */}
                {detailedData && (
                  <>
                    {/*
                      Using a Bootstrap Container for nice margins and padding.
                      Below are Rows and Columns for each supplier attribute.
                      Each Row contains two Columns - one for the attribute name and one for the attribute value.
                      Below this are tables for suppliers' manufacturers and products,
                      with conditional rendering based on whether the respective arrays are not empty.
                      In the tables, each row corresponds to a manufacturer or product.
                      */}
                    <Row>
                      <Col>
                        <p className="fs-6 fw-bold">Website:</p>
                      </Col>
                      <Col>
                        <a
                          href={supplier.website}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <NavigateToUrlIcon />
                        </a>
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <p className="fs-6 fw-bold">Main Email:</p>
                      </Col>
                      <Col>
                        <p> {supplier.email}</p>
                      </Col>
                    </Row>
                    {detailedData?.secondary_emails &&
                      detailedData.secondary_emails.length > 0 && (
                        <Row>
                          <Col>
                            <p className="fs-6 fw-bold">Secondary Emails:</p>
                          </Col>
                          <Col>
                            {detailedData.secondary_emails.map(
                              (secondaryEmail, index) => (
                                <span key={index}>
                                  {secondaryEmail.email}
                                  {index <
                                    detailedData.secondary_emails.length -
                                      1 && <br />}
                                </span>
                              )
                            )}
                          </Col>
                        </Row>
                      )}
                    <Row>
                      <Col>
                        <p className="fs-6 fw-bold">Phone:</p>
                      </Col>
                      <Col>
                        <p>
                          {supplier.phone_prefix} {supplier.phone_suffix}
                        </p>
                      </Col>
                    </Row>
                    {(detailedData?.inner_supplier_id || supplier?.inner_supplier_id) && (
                      <Row>
                        <Col>
                          <p className="fs-6 fw-bold">Inner Supplier ID:</p>
                        </Col>
                        <Col>
                          <p>{detailedData?.inner_supplier_id || supplier?.inner_supplier_id}</p>
                        </Col>
                      </Row>
                    )}
                    <Row>
                      <Col>
                        <p className="fs-6 fw-bold">Manufacturers: </p>
                      </Col>
                    </Row>
                    <Table striped bordered hover>
                      <thead>
                        <tr className="text-center">
                          <th>#</th>
                          <th>Name</th>
                          <th>Website</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailedData?.manufacturers &&
                        detailedData.manufacturers.length > 0 ? (
                          <>
                            {detailedData.manufacturers.map(
                              (manufacturer, index) => (
                                <React.Fragment key={index}>
                                  <tr className="text-center italic-text">
                                    <td>{index + 1}</td>
                                    <td>
                                      <ManufacturerDetailModal
                                        manufacturerObj={manufacturer}
                                        manufacturerId={manufacturer.id}
                                        handleManufacturerUpdateFromSupplierTable={
                                          updateSuppliers
                                        }
                                      />
                                    </td>
                                    <td>
                                      <a
                                        href={manufacturer.website}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        <NavigateToUrlIcon />
                                      </a>
                                    </td>
                                  </tr>
                                </React.Fragment>
                              )
                            )}
                          </>
                        ) : (
                          <tr>
                            <td colSpan="3" className="text-center">
                              {detailedData
                                ? 'No manufacturers found'
                                : 'Loading...'}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                    <Row>
                      <Col>
                        <p className="fs-6 fw-bold">Products: </p>
                      </Col>
                    </Row>
                    <Table striped bordered hover>
                      <thead>
                        <tr className="text-center">
                          <th>#</th>
                          <th>Name</th>
                          <th>Catalogue Number</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailedData?.products &&
                        detailedData.products.length > 0 ? (
                          <>
                            {detailedData.products.map((product, index) => (
                              <React.Fragment key={index}>
                                <tr className="text-center italic-text">
                                  <td>{index + 1}</td>
                                  <td>
                                    <ProductDetailModal
                                      productObj={product}
                                      productId={product.id}
                                      handleProductUpdateFromSupplierTable={
                                        updateSuppliers
                                      }
                                    />
                                  </td>
                                  <td>{product.cat_num}</td>
                                </tr>
                              </React.Fragment>
                            ))}
                          </>
                        ) : (
                          <tr>
                            <td colSpan="3" className="text-center">
                              {detailedData
                                ? 'No products found'
                                : 'Loading...'}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </>
                )}
              </Container>
            )}
          </Modal.Body>

          {supplier && (
            <Modal.Footer className="d-flex flex-row justify-content-between">
              {!isSupplier && !isViewer && (
                <div className="d-flex flex-row">
                  {/* Two buttons - one to open the supplier edit modal, and one to
                  delete the supplier */}
                  <div className="me-2">
                    <SupplierModal
                      supplierObj={supplier}
                      clearSearchValue={clearSearchValue}
                    />
                  </div>
                  <DeleteButton
                    objectType="supplier"
                    objectName={supplier.name}
                    objectId={supplier.id}
                    deleteFetchFunc={deleteSupplier}
                    onSuccessfulDelete={updateSuppliers}
                    clearSearchValue={clearSearchValue}
                  />
                </div>
              )}
              {/* A button to close the modal */}
              <Button variant="secondary" onClick={handleClose}>
                Close
              </Button>
            </Modal.Footer>
          )}
        </Modal>
      )}
    </div>
  );
};
export default SupplierDetailModal;
